import { AlertCircle, ArrowRight, Check } from 'lucide-react';

/* Drei Abschnitte, die die Einwandreihenfolge schliessen.
   Eine Landing Page ist ein Einwandbehandlungs-Dokument: Der Besucher bringt
   sechs bis zwoelf Einwaende mit, und die Seite loest sie in der Reihenfolge
   auf, in der sie auftreten. */

/* "Habe ich dieses Problem?" - und zugleich das Eingestaendnis.
   Nach Kraft 4 der Business Mastery macht "Angreifen und Bekennen" ein
   Viertel der Kommunikation aus: den Einwand vorwegnehmen und zugeben, selbst
   schuldig gewesen zu sein. Der Einwand "Agenturen versprechen viel" ist
   damit erledigt, bevor der Leser ihn denkt. */
export function ConfessionSection() {
  return (
    <section id="bekenntnis" className="px-5 py-16 sm:px-6 md:px-10 md:py-24 lg:px-14" data-testid="confession-section">
      <div className="mx-auto w-full max-w-[1180px]" data-einblenden>
        <div className="grid gap-8 lg:grid-cols-[.82fr_1.18fr] lg:items-start">
          <div className="lg:sticky lg:top-28">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#CC3366]">Offen gesagt</p>
            <h2 className="mt-4 text-[30px] font-bold leading-[1.04] tracking-[-0.035em] text-[#0D2249] sm:text-[38px] md:text-[48px]">
              Wir haben Ärzten ein Problem verkauft, das wir selbst hatten.
            </h2>
          </div>
          <div className="rounded-[28px] border border-[#0D2249]/10 bg-white p-6 shadow-[0_28px_70px_-52px_rgba(13,34,73,.6)] sm:p-9">
        <div className="space-y-4 text-base leading-relaxed text-[#3B4962] md:text-lg">
          <p>
            Bis in den Sommer konnte unser eigenes System nicht sagen, wie viele Beratungen zu
            Behandlungen werden. Der Bericht zeigte null Prozent. Die echte Quote lag bei 33.
          </p>
          <p>
            Der Fehler steckte in den eigenen Daten: Beratungen, Behandlungen und Nachsorge liefen
            alle unter derselben Leistungsart. Damit rechnete jede Auswertung sich selbst kaputt.
          </p>
          <p className="text-[#0D2249]">
            <span className="font-semibold">Genau daraus ist die Messung entstanden, die es sonst nirgends gibt.</span>{' '}
            Wir haben sie gebaut, weil wir sie selbst gebraucht haben &ndash; nicht, weil sie sich gut verkauft.
          </p>
        </div>
        <div className="mt-8 flex items-start gap-3 rounded-[22px] bg-[#FBE8EF] p-5">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#CC3366]" />
          <p className="text-[15px] leading-relaxed text-[#3B4962]">
            Wenn Ihnen jemand verspricht, er könne Ihre Zahlen verbessern, ohne sie je gesehen zu haben,
            dann verkauft er Hoffnung. Wir fangen deshalb mit Ihrer echten Zahl an, bevor irgendetwas
            anderes passiert.
          </p>
        </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* "Wie laeuft das konkret ab?" - StoryBrand nennt das den Plan. Er senkt die
   wahrgenommene Komplexitaet, und ohne ihn bleibt jedes Angebot vage.
   Die drei hervorgehobenen Glieder sind die, die eine Agentur nicht sieht und
   eine Praxissoftware nicht zuordnet. */
const KETTE = [
  { titel: 'Anzeige', text: 'Kampagnen für die Behandlungen, die ausgelastet werden sollen.' },
  { titel: 'Anfrage', text: 'Das Formular schickt sie direkt in Ihre Patientenliste, mit Herkunft.' },
  { titel: 'Beratung', text: 'Termin, Erinnerung, Anamnese und Aufklärung digital vorbereitet.', stark: true },
  { titel: 'Nachfassen', text: 'Wer sich nach der Beratung nicht meldet, geht nicht verloren.', stark: true },
  { titel: 'Behandlung', text: 'Dokumentiert, mit Wert und mit der Quelle, aus der sie kam.', stark: true },
  { titel: 'Nachsorge', text: 'Kontrolltermine und Wiederkehr laufen automatisch weiter.' },
];

export function ChainSection() {
  return (
    <section id="ablauf" className="px-5 py-16 sm:px-6 md:px-10 md:py-24 lg:px-14" data-testid="chain-section">
      <div className="mx-auto w-full max-w-[1180px]" data-einblenden>
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#CC3366]">Der Ablauf</p>
          <h2 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-[#0D2249] md:text-[42px]">
            Von der Anzeige bis zur Nachsorge, in einer Oberfläche.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#3B4962] md:text-lg">
            Die drei markierten Schritte sind die, an denen Patienten heute verschwinden.
            Eine Agentur sieht sie nicht, eine Praxissoftware ordnet sie nicht zu.
          </p>
        </div>

        <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-folge>
          {KETTE.map((glied, i) => (
            <li
              key={glied.titel}
              className={`relative rounded-2xl border p-5 ${
                glied.stark ? 'border-[#CC3366]/40 bg-[#F2F1EF]' : 'border-[#E1E5EC] bg-white'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
                  glied.stark ? 'bg-[#CC3366] text-white' : 'bg-[#E1E5EC] text-[#CC3366]'
                }`}>
                  {i + 1}
                </span>
                <p className="font-semibold text-[#0D2249]">{glied.titel}</p>
                {i < KETTE.length - 1 && (
                  <ArrowRight className="ml-auto h-4 w-4 text-[#C8CFDA]" aria-hidden="true" />
                )}
              </div>
              <p className="mt-3 text-[15px] leading-relaxed text-[#3B4962]">{glied.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* "Was riskiere ich?" - diese vier Punkte standen bereits auf der Seite,
   versteckt in einem Fliesstextabsatz. Der groesste Wettbewerber verkauft eine
   schwaechere Risikoumkehr deutlich lauter. */
const RISIKO = [
  { gross: '0 €', klein: 'Einrichtungsgebühr' },
  { gross: '0 €', klein: 'Datenübernahme aus Ihrem bisherigen System' },
  { gross: 'Persönlich', klein: 'Einrichtung durch uns, kein Chatbot' },
  { gross: 'Monatlich', klein: 'kündbar, keine Mindestlaufzeit' },
];

export function RiskReversalSection() {
  return (
    <section id="risiko" className="px-5 py-16 sm:px-6 md:px-10 md:py-24 lg:px-14" data-testid="risk-section">
      <div className="mx-auto w-full max-w-[1180px]" data-einblenden>
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#CC3366]">Was Sie riskieren</p>
          <h2 className="mt-4 text-[30px] font-bold leading-[1.04] tracking-[-0.035em] text-[#0D2249] sm:text-[38px] md:text-[48px]">
            Nichts, was Sie nicht zurückholen können.
          </h2>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-folge>
          {RISIKO.map((punkt, index) => (
            <div key={punkt.klein} className={`rounded-[26px] border p-6 ${index === 0 ? 'border-[#CC3366] bg-[#CC3366] text-white shadow-[0_24px_60px_-38px_rgba(204,51,102,.7)]' : 'border-[#0D2249]/10 bg-white'}`}>
              <p className={`text-2xl font-bold tracking-[-0.03em] ${index === 0 ? 'text-white' : 'text-[#CC3366]'}`}>{punkt.gross}</p>
              <p className={`mt-2 text-[15px] leading-relaxed ${index === 0 ? 'text-white/75' : 'text-[#3B4962]'}`}>{punkt.klein}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-start gap-3 rounded-[22px] bg-[#F2F1EF] p-5">
          <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#CC3366]" />
          <p className="text-[15px] leading-relaxed text-[#3B4962]">
            Ihre Praxissoftware für Dokumentation und Abrechnung bleibt, wo sie ist.
            Wir schließen die Lücke davor, wir ersetzen nichts.
          </p>
        </div>
      </div>
    </section>
  );
}
